import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardStepper } from '../../components/layout/WizardStepper';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useWizardStore } from '../../store/wizard.store';
import { offersService, type OfferItem } from '../../services/offers.service';
import api from '../../services/api';
import { formatSAR } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';
import { Minus, Plus } from 'lucide-react';

const WIZARD_STEPS = [
  { label: 'Product', path: '/catalog' },
  { label: 'Location & Coverage', path: '/offers/wizard/location' },
  { label: 'Cart', path: '/offers/wizard/cart' },
  { label: 'Customer', path: '/offers/wizard/customer' },
  { label: 'Auth. Person', path: '/offers/wizard/authorized-person' },
  { label: 'Summary', path: '/offers/wizard/summary' },
];

const CONTRACT_OPTIONS = [12, 24, 36];

export const CartStep: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { offerId, offerNumber, removeItem, setStep } = useWizardStore();
  const [cartItems, setCartItems] = useState<OfferItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!offerId) return;
    offersService
      .get(offerId)
      .then((offer) => setCartItems(offer.items ?? []))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [offerId]);

  const handleRemove = async (itemId: string, productId: string) => {
    if (!offerId) return;
    try {
      await api.delete(`/offers/${offerId}/cart/${itemId}`);
      setCartItems((prev) => prev.filter((i) => i.id !== itemId));
      removeItem(productId);
    } catch {
      showToast('Failed to remove item', 'error');
    }
  };

  const handleUpdateItem = async (itemId: string, patch: { quantity?: number; contractMonths?: number }) => {
    if (!offerId) return;
    setUpdating((u) => ({ ...u, [itemId]: true }));
    try {
      const res = await api.patch<OfferItem>(`/offers/${offerId}/cart/${itemId}`, patch);
      setCartItems((prev) => prev.map((i) => i.id === itemId ? { ...i, ...res.data } : i));
    } catch {
      showToast('Failed to update item', 'error');
    } finally {
      setUpdating((u) => ({ ...u, [itemId]: false }));
    }
  };

  const allFailed = cartItems.length > 0 && cartItems.every((i) => i.status === 'Coverage_Failed');
  const total = cartItems.reduce((sum, i) => sum + (Number(i.price) || 0), 0);
  const vatAmount = total * (15 / 115);
  const exclVat = total - vatAmount;

  const handleNext = () => {
    if (cartItems.length === 0) { showToast('Add at least one product to continue', 'warning'); return; }
    if (allFailed) { showToast('All items have coverage failures. Fix coverage before proceeding.', 'error'); return; }
    setStep(3);
    navigate('/offers/wizard/customer');
  };

  const unitPrice = (item: OfferItem) => {
    const qty = item.quantity ?? 1;
    return qty > 1 ? Number(item.price) / qty : Number(item.price);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Your Cart" subtitle={offerNumber ? `Offer #${offerNumber}` : 'Review your selection'} />
      <WizardStepper steps={WIZARD_STEPS} currentStep={2} />
      <main style={{ flex: 1, padding: '28px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '1100px', display: 'flex', gap: '28px', alignItems: 'flex-start' }}>

          {/* LEFT — cart items */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700 }}>Your selection</h3>
                <span style={{ fontSize: '13px', color: 'var(--text-2)' }}>{cartItems.length} item{cartItems.length !== 1 ? 's' : ''}</span>
              </div>

              {loading ? (
                <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-3)' }}>Loading…</div>
              ) : cartItems.length === 0 ? (
                <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-3)' }}>
                  No products selected yet. Go back to add products.
                </div>
              ) : (
                cartItems.map((item, idx) => {
                  const isMobility = item.product?.category === 'Mobility';
                  const qty = item.quantity ?? 1;
                  const contractMos = item.contractMonths ?? 12;
                  const up = unitPrice(item);
                  const isUpdating = !!updating[item.id];

                  return (
                    <div key={item.id} style={{ padding: '20px 24px', borderBottom: idx < cartItems.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        {/* Icon */}
                        <div style={{ width: 52, height: 52, borderRadius: 10, background: 'var(--p-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22 }}>
                          {isMobility ? '📱' : item.product?.category === '5G' ? '🌐' : '📡'}
                        </div>

                        {/* Details */}
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontWeight: 700, fontSize: 15 }}>{item.product?.name ?? 'Product'}</span>
                            <Badge label={item.status} />
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 10 }}>
                            {item.product?.category} · Monthly billing
                          </div>

                          {/* Quantity controls — Mobility only */}
                          {isMobility && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                              <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>Quantity:</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1.5px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                                <button
                                  type="button"
                                  onClick={() => qty > 1 && handleUpdateItem(item.id, { quantity: qty - 1 })}
                                  disabled={qty <= 1 || isUpdating}
                                  style={{ padding: '5px 10px', background: 'transparent', border: 'none', cursor: qty > 1 ? 'pointer' : 'not-allowed', color: qty > 1 ? 'var(--text-1)' : 'var(--text-3)', display: 'flex', alignItems: 'center' }}
                                >
                                  <Minus size={13} />
                                </button>
                                <span style={{ padding: '5px 14px', fontSize: 14, fontWeight: 700, color: 'var(--text-1)', borderLeft: '1.5px solid var(--border)', borderRight: '1.5px solid var(--border)' }}>
                                  {isUpdating ? '…' : qty}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateItem(item.id, { quantity: qty + 1 })}
                                  disabled={qty >= 99 || isUpdating}
                                  style={{ padding: '5px 10px', background: 'transparent', border: 'none', cursor: qty < 99 ? 'pointer' : 'not-allowed', color: 'var(--text-1)', display: 'flex', alignItems: 'center' }}
                                >
                                  <Plus size={13} />
                                </button>
                              </div>
                              {qty > 1 && (
                                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                                  {formatSAR(up)}/unit × {qty}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Contract months selector */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>Contract:</span>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {CONTRACT_OPTIONS.map((mo) => (
                                <button
                                  key={mo}
                                  type="button"
                                  onClick={() => contractMos !== mo && handleUpdateItem(item.id, { contractMonths: mo })}
                                  disabled={isUpdating}
                                  style={{
                                    padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', fontFamily: 'inherit',
                                    background: contractMos === mo ? 'var(--p)' : 'var(--surface-alt)',
                                    color: contractMos === mo ? '#fff' : 'var(--text-2)',
                                    transition: 'all .12s',
                                  }}
                                >
                                  {mo} mo
                                </button>
                              ))}
                            </div>
                          </div>

                          {item.location && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-2)', background: 'var(--surface-alt)', padding: '6px 10px', borderRadius: 6, marginTop: 10 }}>
                              <span style={{ color: 'var(--p)' }}>📍</span>
                              <span>
                                {item.location.addressLine1
                                  ? `${item.location.addressLine1}${item.location.city ? `, ${item.location.city}` : ''}`
                                  : `${Number(item.location.lat).toFixed(4)}, ${Number(item.location.lng).toFixed(4)}`}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Price + remove */}
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-1)' }}>
                            {formatSAR(item.price)}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>/mo</div>
                          <button
                            onClick={() => handleRemove(item.id, item.productId)}
                            style={{ background: 'none', border: '1px solid var(--err-border)', borderRadius: 6, color: 'var(--err)', cursor: 'pointer', fontSize: 12, padding: '4px 10px', fontFamily: 'inherit' }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {allFailed && (
              <div style={{ background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: 10, padding: '14px 18px', color: 'var(--err)', fontSize: 13 }}>
                All items have coverage failures. You cannot proceed until at least one item has coverage.
              </div>
            )}

            <Button variant="ghost" onClick={() => navigate('/catalog')} style={{ alignSelf: 'flex-start' }}>
              + Add Another Product
            </Button>
          </div>

          {/* RIGHT — pricing summary */}
          <div style={{ width: '320px', flexShrink: 0, position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ background: 'var(--p)', color: '#fff', padding: '14px 20px', fontSize: 13.5, fontWeight: 700 }}>
                Monthly Payment
              </div>
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cartItems.map((item) => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-2)', flex: 1, marginRight: 8 }}>
                      {item.product?.name ?? 'Product'}
                      {(item.quantity ?? 1) > 1 && <span style={{ color: 'var(--text-3)', fontSize: 11 }}> ×{item.quantity}</span>}
                    </span>
                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatSAR(item.price)}</span>
                  </div>
                ))}

                {cartItems.length > 0 && (
                  <>
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-3)' }}>
                      <span>Subtotal (excl. VAT)</span>
                      <span>{formatSAR(exclVat)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-3)' }}>
                      <span>VAT (15%)</span>
                      <span>{formatSAR(vatAmount)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, color: 'var(--text-1)', borderTop: '2px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
                      <span>Total</span>
                      <span style={{ color: 'var(--p)' }}>{formatSAR(total)}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-3)', textAlign: 'center', marginTop: 2 }}>
                      Monthly billing · Min. contract per item above
                    </div>
                  </>
                )}

                {cartItems.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '12px 0', fontSize: 13 }}>No items yet</div>
                )}
              </div>
            </div>

            <Button onClick={handleNext} disabled={cartItems.length === 0 || allFailed} style={{ width: '100%', padding: '14px' }}>
              Continue to Customer Info
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};
